import type { Edulead } from 'edulead';
import { ActivityFeedItem } from '../services/pusher.service';

/** The legacy event type each /v4 one replaces; live items from Pusher still carry these. */
const LEGACY_EVENT_TYPES: Record<Edulead.EduleadV4FeedItemEventType, string> = {
  FEED_ITEM_EVENT_TYPE_UNSPECIFIED: 'UNKNOWN',
  FEED_ITEM_EVENT_TYPE_PERSON_OBSERVED: 'CONTACT_OBSERVED',
  FEED_ITEM_EVENT_TYPE_EMAIL_VERIFIED: 'EMAIL_VERIFIED',
  FEED_ITEM_EVENT_TYPE_PERSON_FIELD_CHANGED: 'CONTACT_FIELD_CHANGED',
};

/** The parts a feed item and a person's activity share. */
type FeedEvent = Pick<
  Edulead.EduleadV4FeedItem,
  'id' | 'event_type' | 'person' | 'observation' | 'email_verification' | 'field_change'
>;

/**
 * A /v4 feed item or person activity in the legacy shape the feed components render. Live items
 * keep arriving over Pusher in that shape (api/v4/feed_items.md §7 in the Go repo), so both feed
 * one view model. `deliveredAt` is the time shown: the feed's delivery, a person's observation.
 */
export function toActivityFeedItem(
  item: FeedEvent,
  personId: string,
  deliveredAt: string,
): ActivityFeedItem {
  const person = item.person ?? {};
  const contact = {
    id: personId,
    firstName: person.first_name || null,
    lastName: person.last_name || null,
    title: person.title || null,
    belongsTo: person.institution_name
      ? [{ institution: { name: person.institution_name, state: person.institution_state ?? '' } }]
      : [],
  };
  const { observation, email_verification: verification, field_change: change } = item;
  let payload: unknown = { contact };
  if (observation) {
    payload = {
      contact,
      observation: {
        kind:
          observation.kind === 'FEED_ITEM_OBSERVATION_KIND_NEW_PERSON'
            ? 'NEW_CONTACT'
            : 'EXISTING_CONTACT',
        type: observation.source === 'FEED_ITEM_OBSERVATION_SOURCE_FOIA' ? 'FOIA' : 'CRAWLER',
        observedAt: observation.observe_time ?? '',
        normalizedTitle: observation.title || null,
        url: observation.url || null,
        screenshotKey: observation.screenshot_key || null,
      },
    };
  } else if (verification) {
    payload = {
      contact,
      verification: {
        email: verification.email ?? '',
        status: verification.status ?? '',
        verifiedAt: verification.verify_time ?? '',
      },
    };
  } else if (change) {
    payload = {
      contact,
      change: {
        contactId: personId,
        field: change.field ?? '',
        oldValue: change.old_value ?? '',
        newValue: change.new_value ?? '',
        changedAt: change.change_time ?? '',
      },
    };
  }
  return {
    id: item.id ?? '',
    eventType: LEGACY_EVENT_TYPES[item.event_type ?? 'FEED_ITEM_EVENT_TYPE_UNSPECIFIED'],
    entityType: 'CANONICAL_CONTACT',
    entityId: personId,
    payload,
    deliveredAt,
  };
}
